import { useMemo, useState } from "react";
import {
  Award,
  BookOpen,
  CalendarDays,
  Check,
  Copy,
  ExternalLink,
  FileText,
  Gauge,
  ListChecks,
  Monitor,
  Sparkles,
} from "lucide-react";
import type { LibraryItem } from "../types/navigator";
import {
  DetailRow,
  GlanceCell,
  RichHtml,
  Row,
  formatDetailDate,
  htmlToPlainText,
} from "./DetailParts";

function isPromptAsset(assetType?: string): boolean {
  return !!assetType && /prompt/i.test(assetType);
}

function CopyButton({ html }: { html: string }) {
  const [copied, setCopied] = useState(false);
  const plain = useMemo(() => htmlToPlainText(html), [html]);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(plain);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard blocked — the prompt is still selectable on screen.
    }
  };
  return (
    <button
      type="button"
      className={`copy-btn${copied ? " is-copied" : ""}`}
      onClick={onCopy}
      aria-label="Copy prompt"
    >
      {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
      <span>{copied ? "Copied" : "Copy"}</span>
    </button>
  );
}

export function LibraryDetails({ item }: { item: LibraryItem }) {
  const isPrompt = isPromptAsset(item.assetType);
  const submittedBy = (item.raw?._createdByName as string | undefined) || "";
  const platform = item.url ? (
    <a className="ini-link" href={item.url} target="_blank" rel="noopener noreferrer">
      {item.urlLabel || item.aiPlatform || "Open"}
      <ExternalLink size={12} aria-hidden />
    </a>
  ) : (
    item.aiPlatform
  );
  const glance = [
    item.assetType ? (
      <GlanceCell key="type" icon={BookOpen} label="Asset type">
        {item.assetType}
      </GlanceCell>
    ) : null,
    item.aiPlatform || item.url ? (
      <GlanceCell key="platform" icon={Monitor} label="Platform">
        {platform}
      </GlanceCell>
    ) : null,
    item.complexity ? (
      <GlanceCell key="complexity" icon={Gauge} label="Complexity">
        {item.complexity}
      </GlanceCell>
    ) : null,
    item.benefitType ? (
      <GlanceCell key="benefit" icon={Award} label="Benefit">
        {item.benefitType}
      </GlanceCell>
    ) : null,
  ].filter(Boolean);

  return (
    <div className="ini">
      {glance.length ? (
        <div className="ini-glance">
          <div className="ini-glance-row">{glance}</div>
        </div>
      ) : null}

      <dl className="ini-rows">
        <Row icon={FileText} label="Description" text={item.description} />
        {item.instructions ? (
          isPrompt ? (
            <Row icon={Sparkles} label="Prompt" action={<CopyButton html={item.instructions} />}>
              <div className="ini-prompt">
                <RichHtml html={item.instructions} />
              </div>
            </Row>
          ) : (
            <Row icon={ListChecks} label="How to use">
              <RichHtml html={item.instructions} />
            </Row>
          )
        ) : null}
      </dl>

      <section className="ini-section">
        <h4 className="ini-section-title">
          <CalendarDays size={13} aria-hidden /> Details
        </h4>
        <dl className="ini-details">
          <DetailRow label="Department" value={item.department} />
          <DetailRow label="Submitted by" value={submittedBy} />
          <DetailRow label="Status" value={item.status} />
          <DetailRow label="License" value={item.license} />
          <DetailRow label="Submitted" value={formatDetailDate(item.created)} />
          <DetailRow label="Last updated" value={formatDetailDate(item.modified)} />
        </dl>
      </section>
    </div>
  );
}

